import { useState, useCallback, useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { TRPCClientError } from "@trpc/client";
import { MAX_AI_FORM_ASSIST_MESSAGE_LENGTH } from "@api/shared-runtime/ai-form-assist";
import { useTRPC, trpcClient } from "@/trpc";
import { useOrganization } from "@/hooks/use-organization";
import { tusUploadFile } from "@/ajax/upload/tusUploadFile";
import { contentTypeFor } from "@/common/utils/upload-content-type";
import { declareUploadFile } from "@/ajax/upload/declare-upload-file";
import type { FieldValues, UseFormReturn } from "react-hook-form";

/** An attached file, still local — not uploaded until send is clicked. */
export interface AIAttachment {
  /** Client-side id so we can remove before sending. */
  tempId: string;
  file: File;
  fileName: string;
  mimeType: string;
  fileSizeBytes: number;
  /** Validation error from when the file was picked. Non-null = can't be sent. */
  errorMessage?: string;
}

export interface AIMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  /** Whether this message included form changes that were auto-applied */
  appliedChanges: boolean;
  /** Attachments the user sent with this message (for display only). */
  attachments?: Array<{ fileName: string; mimeType: string }>;
}

interface UseAIFormAssistantOptions<T extends FieldValues> {
  formType: "hiringProcessTemplate" | "orgForm" | "jobPost";
  form: UseFormReturn<T>;
  /**
   * When true, the AI's returned formState is merged with the current form
   * values (fields not present in the response are preserved). When false
   * (default), the form is fully reset to the AI's state. Use merge mode
   * when the AI schema only covers a subset of the form (e.g. job post,
   * where entity refs like facility/specialties are edited elsewhere).
   */
  mergeFormState?: boolean;
  /**
   * Extra context to send with every AI request. Use this to supply
   * option lists (e.g. available specialties, healthcare professional types)
   * so the AI can pick valid IDs when populating reference fields.
   */
  additionalContext?: Record<string, unknown>;
  /**
   * Optional form-specific normalization before applying an AI response.
   * Useful for preserving local-only or persistence IDs around array resets.
   */
  transformFormState?: (nextState: T, currentState: T) => T;
}

export const MAX_AI_FORM_ASSIST_ATTACHMENTS = 5;
const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;
const ALLOWED_MIME_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/heic",
  "image/heif",
  "image/webp",
]);
type AllowedMimeType =
  | "application/pdf"
  | "image/jpeg"
  | "image/png"
  | "image/heic"
  | "image/heif"
  | "image/webp";

function validateFile(file: File): string | null {
  const mimeType = contentTypeFor(file);
  if (!ALLOWED_MIME_TYPES.has(mimeType)) {
    return `"${file.name}" — unsupported file type. PDFs and images only.`;
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return `"${file.name}" — file too large (max 20MB).`;
  }
  return null;
}

function getAttachmentLimitMessage(maximum = MAX_AI_FORM_ASSIST_ATTACHMENTS) {
  return `You can attach up to ${maximum} files per message.`;
}

function getMessageLengthLimitMessage(
  maximum = MAX_AI_FORM_ASSIST_MESSAGE_LENGTH,
) {
  return `Your message is too long. Please shorten it to ${maximum.toLocaleString()} characters or fewer`;
}

function getZodInputErrorMessage(errorMessage: string): string | null {
  try {
    const issues = JSON.parse(errorMessage) as unknown;
    if (!Array.isArray(issues)) {
      return null;
    }

    for (const issue of issues) {
      if (!issue || typeof issue !== "object") {
        return null;
      }
      const candidate = issue as {
        code?: unknown;
        maximum?: unknown;
        path?: unknown;
      };
      if (candidate.code !== "too_big" || !Array.isArray(candidate.path)) {
        continue;
      }
      const maximum =
        typeof candidate.maximum === "number" ? candidate.maximum : undefined;
      if (candidate.path.includes("userMessage")) {
        return getMessageLengthLimitMessage(maximum);
      }
      if (candidate.path.includes("attachmentIds")) {
        return getAttachmentLimitMessage(maximum);
      }
    }
  } catch {
    return null;
  }

  return null;
}

function getAssistErrorMessage(error: unknown): string {
  const fallback = error instanceof Error ? error.message : String(error);
  const zodInputMessage = getZodInputErrorMessage(fallback);
  if (zodInputMessage) {
    return zodInputMessage;
  }

  if (error instanceof TRPCClientError) {
    const data = error.data as
      | { code?: string; httpStatus?: number }
      | null
      | undefined;
    if (data?.httpStatus === 400 || data?.code === "BAD_REQUEST") {
      return "That request was invalid. Please check your message and attachments and try again.";
    }
  }

  return fallback;
}

export function useAIFormAssistant<T extends FieldValues>({
  formType,
  form,
  mergeFormState = false,
  additionalContext,
  transformFormState,
}: UseAIFormAssistantOptions<T>) {
  const trpc = useTRPC();
  const { selectedOrgBusinessUnit } = useOrganization();
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [pendingAttachments, setPendingAttachments] = useState<AIAttachment[]>(
    [],
  );
  const pendingAttachmentCountRef = useRef(0);
  /** True while we're uploading attachments + awaiting the AI response. */
  const [isUploading, setIsUploading] = useState(false);

  const mutation = useMutation(trpc.aiFormAssist.assist.mutationOptions());

  useEffect(() => {
    pendingAttachmentCountRef.current = pendingAttachments.length;
  }, [pendingAttachments.length]);

  const applyAssistResult = useCallback(
    (data: { message: string; formState: unknown }) => {
      const hasFormState = data.formState !== null;

      if (hasFormState) {
        const currentState = form.getValues();
        let nextState: T;
        if (mergeFormState) {
          // Strip null values so the AI can't clobber existing fields with
          // null (which breaks controlled <Input> bindings and usually
          // isn't what the user wants). To clear a text field the AI
          // should emit "" instead.
          const changes = Object.fromEntries(
            Object.entries(data.formState as Record<string, unknown>).filter(
              ([, v]) => v !== null,
            ),
          );
          nextState = {
            ...form.getValues(),
            ...changes,
          } as unknown as T;
        } else {
          nextState = data.formState as unknown as T;
        }
        if (transformFormState) {
          nextState = transformFormState(nextState, currentState);
        }
        form.reset(nextState);
      }

      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: "assistant",
          content: data.message,
          appliedChanges: hasFormState,
        },
      ]);
    },
    [form, mergeFormState, transformFormState],
  );

  const appendErrorMessage = useCallback((errorMessage: string) => {
    setMessages((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        role: "assistant",
        content: `Sorry, I hit an error: ${errorMessage}. Please try again.`,
        appliedChanges: false,
      },
    ]);
  }, []);

  /** Add a file to the pending list. Files are uploaded only when send is clicked. */
  const addAttachment = useCallback(async (file: File) => {
    if (pendingAttachmentCountRef.current >= MAX_AI_FORM_ASSIST_ATTACHMENTS) {
      throw new Error(getAttachmentLimitMessage());
    }

    pendingAttachmentCountRef.current += 1;
    const errorMessage = validateFile(file) ?? undefined;
    setPendingAttachments((prev) => {
      return [
        ...prev,
        {
          tempId: crypto.randomUUID(),
          file,
          fileName: file.name,
          mimeType: contentTypeFor(file),
          fileSizeBytes: file.size,
          errorMessage,
        },
      ];
    });
  }, []);

  const removeAttachment = useCallback((tempId: string) => {
    setPendingAttachments((prev) => {
      const next = prev.filter((a) => a.tempId !== tempId);
      pendingAttachmentCountRef.current = next.length;
      return next;
    });
  }, []);

  /**
   * Upload one attachment and return its server id. Throws if anything fails.
   */
  const uploadAttachment = useCallback(
    async (att: AIAttachment): Promise<string> => {
      const declared = await declareUploadFile(att.file);
      const created = await trpcClient.aiFormAssist.createAttachment.mutate({
        organizationBusinessUnitId: selectedOrgBusinessUnit.id,
        ...declared,
        mimeType: declared.mimeType as AllowedMimeType,
      });

      await tusUploadFile({
        file: att.file,
        bucketName: created.bucketName,
        objectName: created.storagePath,
        uploadToken: created.uploadToken,
      });

      return created.id;
    },
    [selectedOrgBusinessUnit.id],
  );

  const sendMessage = useCallback(
    async (text: string) => {
      // Starter prompts and other callers can bypass the composer validation.
      // Reject before consuming pending attachments or starting any uploads.
      if (text.length > MAX_AI_FORM_ASSIST_MESSAGE_LENGTH) {
        appendErrorMessage(getMessageLengthLimitMessage());
        return;
      }

      // Snapshot attachments so we can display them on the user message
      // even after we clear the pending list.
      const attachmentsToSend = pendingAttachments.filter(
        (a) => !a.errorMessage,
      );

      // Add the user message to the conversation.
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: "user",
          content: text,
          appliedChanges: false,
          attachments:
            attachmentsToSend.length > 0
              ? attachmentsToSend.map((a) => ({
                  fileName: a.fileName,
                  mimeType: a.mimeType,
                }))
              : undefined,
        },
      ]);

      // Build conversation history from prior messages.
      const conversationHistory = messages.map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));

      // Clear pending attachments optimistically — they're now part of the
      // user message above. If upload fails we'll surface an error via the
      // assistant message.
      pendingAttachmentCountRef.current = 0;
      setPendingAttachments([]);

      try {
        let attachmentIds: string[] | undefined;
        if (attachmentsToSend.length > 0) {
          setIsUploading(true);
          try {
            attachmentIds = await Promise.all(
              attachmentsToSend.map((att) => uploadAttachment(att)),
            );
          } catch (uploadErr) {
            const message =
              uploadErr instanceof Error
                ? uploadErr.message
                : String(uploadErr);
            appendErrorMessage(`couldn't upload your attachments (${message})`);
            return;
          } finally {
            setIsUploading(false);
          }
        }

        try {
          const result = await mutation.mutateAsync({
            organizationBusinessUnitId: selectedOrgBusinessUnit.id,
            formType,
            currentFormState: form.getValues() as Record<string, unknown>,
            additionalContext,
            conversationHistory,
            userMessage: text,
            attachmentIds,
          });
          applyAssistResult(result);
        } catch (assistErr) {
          appendErrorMessage(getAssistErrorMessage(assistErr));
        }
      } finally {
        setIsUploading(false);
      }
    },
    [
      pendingAttachments,
      messages,
      mutation,
      selectedOrgBusinessUnit.id,
      formType,
      form,
      additionalContext,
      uploadAttachment,
      applyAssistResult,
      appendErrorMessage,
    ],
  );

  const clearConversation = useCallback(() => {
    setMessages([]);
    pendingAttachmentCountRef.current = 0;
    setPendingAttachments([]);
  }, []);

  const hasInvalidAttachment = pendingAttachments.some((a) => !!a.errorMessage);

  return {
    messages,
    sendMessage,
    isLoading: mutation.isPending || isUploading,
    clearConversation,
    pendingAttachments,
    addAttachment,
    removeAttachment,
    /** Disable send while any attachment has a validation error. */
    hasInvalidAttachment,
  };
}
